// ScopesPage.tsx — pivot view of scopes derived from role permissions.
import { Fragment, ReactNode, useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { BsPlusLg, BsTrash, BsX } from 'react-icons/bs'
import Combobox from 'common/ui/Combobox'
import Expando, { ExpandoChevron } from 'common/ui/Expando'
import NameTag from '../components/NameTag'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface RoleData {
    name: string
    description?: string
    permissions: string[]
}

// Tree node for the dotted-path scope tree. `isScope` is true iff the node's
// fullPath is itself a defined scope (a node may be both a scope and a parent
// of deeper scopes — e.g. `authz` and `authz.secrets`). Single-child chains
// of non-scope nodes get compressed into one row.
interface ScopeTreeNode {
    label: string
    fullPath: string
    isScope: boolean
    children: ScopeTreeNode[]
}

const scopeTreeCompare = (a: ScopeTreeNode, b: ScopeTreeNode): number => {
    const aLeaf = a.children.length === 0
    const bLeaf = b.children.length === 0
    if (aLeaf !== bLeaf) return aLeaf ? 1 : -1
    return a.label.localeCompare(b.label)
}

const buildScopeTree = (scopes: string[]): ScopeTreeNode[] => {
    interface Raw {
        segment: string
        children: Map<string, Raw>
        isScope: boolean
    }
    const root: Raw = { segment: '', children: new Map(), isScope: false }
    for (const s of scopes) {
        let node = root
        for (const part of s.split('.').filter(Boolean)) {
            if (!node.children.has(part)) node.children.set(part, { segment: part, children: new Map(), isScope: false })
            node = node.children.get(part)!
        }
        node.isScope = true
    }
    const compress = (raw: Raw, parentPath: string): ScopeTreeNode => {
        const labels = [raw.segment]
        let cur = raw
        while (!cur.isScope && cur.children.size === 1) {
            const next = cur.children.values().next().value as Raw
            labels.push(next.segment)
            cur = next
        }
        const label = labels.filter(Boolean).join('.')
        const fullPath = parentPath ? `${parentPath}.${label}` : label
        const children = Array.from(cur.children.values())
            .map((c) => compress(c, fullPath))
            .sort(scopeTreeCompare)
        return { label, fullPath, isScope: cur.isScope, children }
    }
    return Array.from(root.children.values())
        .map((c) => compress(c, ''))
        .sort(scopeTreeCompare)
}

const countDescendantScopes = (node: ScopeTreeNode): number => {
    let n = node.isScope ? 1 : 0
    for (const c of node.children) n += countDescendantScopes(c)
    return n
}

interface ScopeFolderRowProps {
    node: ScopeTreeNode
    depth: number
    renderChildren: (depth: number) => ReactNode
}

const ScopeFolderRow = ({ node, depth, renderChildren }: ScopeFolderRowProps) => {
    const count = countDescendantScopes(node)
    return (
        <Expando>
            {({ open, toggle }) => (
                <Fragment>
                    <tr>
                        <td className="align-middle">
                            <div style={{ paddingLeft: `${depth * 1.5}em` }} className="d-flex align-items-center gap-2">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-muted"
                                    onClick={toggle}
                                    aria-expanded={open}
                                    aria-label={open ? 'Collapse group' : 'Expand group'}
                                >
                                    <ExpandoChevron open={open} />
                                </Button>
                                <code className="text-muted">{node.fullPath}.*</code>
                            </div>
                        </td>
                        <td className="text-muted">{count} scope{count === 1 ? '' : 's'}</td>
                    </tr>
                    {open && renderChildren(depth + 1)}
                </Fragment>
            )}
        </Expando>
    )
}

const ScopesPage = () => {
    const navigate = useNavigate()
    const { email, loading, error: authError } = useAuth()
    const [allRoles, setAllRoles] = useState<RoleData[]>([])
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [newScope, setNewScope] = useState('')
    const [newScopeRole, setNewScopeRole] = useState('')
    const [addRoleFor, setAddRoleFor] = useState<string | null>(null)
    const [addRolePick, setAddRolePick] = useState('')
    const [search, setSearch] = useState('')

    const fetchRoles = useCallback(async () => {
        try {
            const res = await apiFetch('/api/admin/roles')
            if (!res.ok) return
            const data = await res.json()
            setAllRoles(data.roles || [])
        } catch { /* user may lack admin scopes */ }
    }, [])

    useEffect(() => {
        if (!loading && email) fetchRoles()
    }, [loading, email, fetchRoles])

    const patchRole = async (role: RoleData, permissions: string[]) => {
        const res = await apiFetch(`/api/admin/roles/${encodeURIComponent(role.name)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ permissions }),
        })
        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            throw new Error(data.error || `Failed to update role "${role.name}"`)
        }
    }

    const grantScope = async (scope: string, roleName: string) => {
        setError(''); setSuccess('')
        const role = allRoles.find((r) => r.name === roleName)
        if (!role) { setError(`Role "${roleName}" not found`); return }
        const perms = role.permissions || []
        if (perms.includes(scope)) {
            setError(`Role "${roleName}" already grants ${scope}`)
            return
        }
        try {
            await patchRole(role, [...perms, scope])
            setSuccess(`Granted ${scope} to ${roleName}`)
            fetchRoles()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to grant scope')
        }
    }

    const revokeScope = async (scope: string, roleName: string) => {
        setError(''); setSuccess('')
        const role = allRoles.find((r) => r.name === roleName)
        if (!role) return
        const newPerms = (role.permissions || []).filter((p) => p !== scope)
        try {
            await patchRole(role, newPerms)
            setSuccess(`Removed ${scope} from ${roleName}`)
            fetchRoles()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove scope')
        }
    }

    const handleNewScope = async (e: React.FormEvent) => {
        e.preventDefault()
        const scope = newScope.trim()
        if (!scope) { setError('Scope name is required'); return }
        if (!newScopeRole) { setError('Pick a role to grant the scope to'); return }
        await grantScope(scope, newScopeRole)
        setNewScope('')
        setNewScopeRole('')
    }

    const handleAddRoleSubmit = async (scope: string) => {
        if (!addRolePick) return
        await grantScope(scope, addRolePick)
        setAddRoleFor(null)
        setAddRolePick('')
    }

    const scopeToRoles = useMemo(() => {
        const m: Record<string, string[]> = {}
        for (const role of allRoles) {
            for (const perm of role.permissions || []) {
                if (!m[perm]) m[perm] = []
                m[perm].push(role.name)
            }
        }
        return m
    }, [allRoles])

    const sortedScopes = useMemo(() => Object.keys(scopeToRoles).sort(), [scopeToRoles])
    const allRoleNames = useMemo(() => allRoles.map((r) => r.name), [allRoles])

    const filteredScopes = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return sortedScopes
        return sortedScopes.filter((s) =>
            s.toLowerCase().includes(q)
            || (scopeToRoles[s] || []).some((r) => r.toLowerCase().includes(q))
        )
    }, [sortedScopes, scopeToRoles, search])

    const scopeTree = useMemo(() => buildScopeTree(filteredScopes), [filteredScopes])

    const renderScopeNodes = (nodes: ScopeTreeNode[], depth: number): ReactNode =>
        nodes.map((node) =>
            node.children.length === 0 && node.isScope
                ? renderScopeLeaf(node, depth)
                : (
                    <ScopeFolderRow
                        key={node.fullPath}
                        node={node}
                        depth={depth}
                        renderChildren={(d) => renderScopeNodes(node.children, d)}
                    />
                ),
        )

    const renderScopeLeaf = (node: ScopeTreeNode, depth: number): ReactNode => {
        const scope = node.fullPath
        const grantingRoles = scopeToRoles[scope] || []
        const remainingRoles = allRoleNames.filter((n) => !grantingRoles.includes(n))
        return (
            <tr key={node.fullPath}>
                <td className="align-middle">
                    <div style={{ paddingLeft: `${depth * 1.5}em` }} className="d-flex align-items-center gap-2">
                        <span style={{ display: 'inline-block', width: '1.5em' }} aria-hidden />
                        <NameTag
                            name={scope}
                            kind="scope"
                            to={`/scopes/${encodeURIComponent(scope)}`}
                        />
                    </div>
                </td>
                <td>
                    <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1">
                        {grantingRoles.map((r) => (
                            <NameTag
                                key={r}
                                name={r}
                                kind="role"
                                to={`/roles/${encodeURIComponent(r)}`}
                                actions={[{ icon: <BsTrash />, onClick: () => revokeScope(scope, r), label: `Remove ${scope} from ${r}`, tone: 'danger' }]}
                            />
                        ))}
                        {addRoleFor === scope ? (
                            <span className="d-inline-flex align-items-center gap-2">
                                <Combobox
                                    value={addRolePick}
                                    onChange={setAddRolePick}
                                    options={remainingRoles}
                                    placeholder="role.name"
                                    style={{ width: '14em' }}
                                />
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleAddRoleSubmit(scope)}
                                    disabled={!addRolePick.trim()}
                                    className="d-flex align-items-center gap-1"
                                ><BsPlusLg /> Add</Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => { setAddRoleFor(null); setAddRolePick('') }}
                                    className="d-flex align-items-center gap-1"
                                ><BsX /> Cancel</Button>
                            </span>
                        ) : remainingRoles.length > 0 && (
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => { setAddRoleFor(scope); setAddRolePick('') }}
                                title="Grant this scope to another role"
                                className="d-flex align-items-center gap-1"
                            ><BsPlusLg /> role</Button>
                        )}
                    </div>
                </td>
            </tr>
        )
    }

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const q = search.trim()
        if (!q) return
        if (sortedScopes.includes(q)) navigate(`/scopes/${encodeURIComponent(q)}`)
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <>
            <h1 className="mb-4">Manage Scopes</h1>
            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Header>
                    Scopes ({filteredScopes.length}{search ? ` of ${sortedScopes.length}` : ''})
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSearchSubmit} className="mb-3">
                        <Form.Group>
                            <Form.Label>Search</Form.Label>
                            <Combobox
                                value={search}
                                onChange={setSearch}
                                options={sortedScopes}
                                style={{ width: "100%" }}
                                placeholder="filter by scope or granting role — Enter on an exact scope to open it"
                            />
                        </Form.Group>
                    </Form>
                    {filteredScopes.length > 0 ? (
                        <Table size="sm" hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th style={{ width: '40%' }}>Scope</th>
                                    <th>Granted by</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderScopeNodes(scopeTree, 0)}
                            </tbody>
                        </Table>
                    ) : (
                        <span className="text-muted small">
                            {sortedScopes.length === 0
                                ? 'No scopes found. Use the form below, or create a role with permissions on the Roles page.'
                                : `No scopes match "${search}"`}
                        </span>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>Grant Scope</Card.Header>
                <Card.Body>
                    <Form id="grant-scope-form" onSubmit={handleNewScope} className="mb-2">
                        <Form.Group className="mb-3">
                            <Form.Label>Scope</Form.Label>
                            <Combobox
                                value={newScope}
                                onChange={setNewScope}
                                options={sortedScopes}
                                style={{ width: "100%" }}
                                placeholder="openfactory.machines.read"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Grant to role</Form.Label>
                            <Combobox
                                value={newScopeRole}
                                onChange={setNewScopeRole}
                                options={allRoleNames}
                                style={{ width: "100%" }}
                                placeholder="role.name (autocompletes from existing roles)"
                                required
                            />
                        </Form.Group>
                    </Form>
                    <small className="text-muted">
                        Scopes exist only via role permissions. Granting adds the scope to the
                        chosen role; removing the last role unrefs the scope.
                    </small>
                </Card.Body>
                <Card.Footer>
                    <Button variant="success" type="submit" form="grant-scope-form" className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Add
                    </Button>
                </Card.Footer>
            </Card>

        </>
    )
}

export default ScopesPage
